<template>
  <section v-if="contextData">
    <div class="container-wrap py-9 tablet:py-12">
      <div class="mb-6 tablet:mb-0 tablet:flex tablet:items-center">
        <div class="tablet:w-1/2 tablet:pr-2 desktop:pr-3">
          <h2 class="text-solution-h2 mb-6 tablet:line-clamp-2">
            {{ contextData.title }}
          </h2>
          <p class="mb-6 w-full text-sm text-gray-600 tablet:line-clamp-[7]">
            {{ contextData.description }}
          </p>
          <div v-if="!isMobile" class="flex flex-wrap">
            <TheButton
              v-if="button01?.text"
              :id="button01?.trackingCode"
              :to="provideFormatUrl(button01.link)"
              :title="button01.text"
              class="mb-3 w-full tablet:mr-2.5 tablet:w-[180px] desktop:mr-6"
            >
              <span class="text-sm font-bold">
                {{ button01.text }}
              </span>
            </TheButton>
            <TheButton
              v-if="button02?.text"
              :id="button02?.trackingCode"
              :to="provideFormatUrl(button02.link)"
              :title="button02.text"
              type="outline"
              class="mb-3 w-full tablet:w-[180px]"
            >
              <span class="text-sm font-bold">
                {{ button02.text }}
              </span>
            </TheButton>
          </div>
        </div>
        <div class="tablet:w-1/2 tablet:pl-2 desktop:pl-3">
          <LightboxVideo
            v-if="contextData.wistiaCode"
            :video-data="video"
            class="aspect-video w-full rounded"
          >
            <template #hidden>
              <h3>{{ contextData.title }}</h3>
              <p>{{ contextData.description }}</p>
            </template>
          </LightboxVideo>
          <figure
            v-else
            class="relative block aspect-video w-full overflow-hidden rounded"
          >
            <img
              :src="contextThumbnail"
              :alt="contextData.title || contextData.description"
              loading="lazy"
              class="pointer-events-none absolute left-0 top-0 block h-full w-full object-cover"
            />
          </figure>
        </div>
      </div>
      <div v-if="isMobile" class="flex flex-wrap">
        <TheButton
          v-if="button01?.text"
          :id="button01?.trackingCode"
          :to="provideFormatUrl(button01.link)"
          :title="button01.text"
          class="mb-3 w-full"
        >
          <span class="text-sm font-bold">
            {{ button01.text }}
          </span>
        </TheButton>
        <TheButton
          v-if="button02?.text"
          :id="button02?.trackingCode"
          :to="provideFormatUrl(button02.link)"
          :title="button02.text"
          type="outline"
          class="w-full"
        >
          <span class="text-sm font-bold">
            {{ button02.text }}
          </span>
        </TheButton>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
  import type { IVideoData } from '@/components/Shared/Lightbox/Video.vue';
  import type { ISuiteProps } from '@/components/Shared/Suite/type.ts';

  interface IButton {
    text: string;
    link: string;
    trackingCode?: string;
  }

  interface IOverviewData {
    title: string;
    description: string;
    buttons: IButton[];
    thumbnail: string;
    wistiaCode?: string;
  }

  interface IOverview {
    component: 'overview';
    meta: {
      navigable?: string;
    };
    data: IOverviewData;
  }

  type IProps = ISuiteProps<IOverview>;

  const props = defineProps<IProps>();

  const { isMobile } = useScreen();
  const { provideFormatUrl } = useFormatUrl();
  const { provideResponsiveImgUrl } = useSolutionsData();

  const video = computed(
    (): IVideoData => ({
      title: contextData.value.title,
      description: contextData.value.description,
      url: contextData.value.wistiaCode || '',
      imageUrl: contextThumbnail.value,
    })
  );

  const contextData = computed((): IOverviewData => props.suiteData.data);

  const contextThumbnail = computed((): string => {
    const { thumbnail } = contextData.value;
    if (!thumbnail) return '';

    if (props.urlConnector === 'solution') {
      return provideResponsiveImgUrl(thumbnail);
    }
    return thumbnail;
  });

  const button01 = computed((): IButton | undefined => {
    return contextData.value.buttons[0];
  });

  const button02 = computed((): IButton | undefined => {
    return contextData.value.buttons[1];
  });

  export type { IOverview, IOverviewData };
</script>
